import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
} from '@angular/core';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasSectionType } from '../gas-section/gas-section.interface';
import { GasAccordionSectionDirective } from './gas-accordion-section.directive';
import { GasIcon } from '../../utils';
import { GasColor } from '../../core/colors/color.namespace';

/** @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=80%3A7 */

/**
 * @description Директива для создания секций внутри аккордиона
 * @param activeName - имя/имена открытой/открытых секции/секций
 * @param multiple - можно ли открывать несколько секций одновременно
 * @param type - внешний вид секции
 * @param activeNameChange - output возвращает имя (имена) активной секции (секций)
 * @example <div
              gas-accordion
              (activeNameChange)="activeNameChange($event)"
              [(activeName)]="active"
              [activeName]="active"
              [multiple]="multiple"
              [type]="type"
              class="mb-md"
            >
              <ng-template gasAccordionSection label="Accordion Title A">
                This is the content area for section A
              </ng-template>
            </div>
 */
@Component({
  selector: 'gas-accordion, [gas-accordion]',
  templateUrl: './gas-accordion.component.html',
  styleUrls: ['./gas-accordion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasAccordionComponent {
  /** Определяет открытую секцию */
  @Input()
  public activeName: string | string[];

  /** Определяет, могут ли одновременно быть открыты несколько секций */
  @GasInputBoolean()
  @Input()
  public multiple: string | boolean = false;

  /** Внешний вид аккордиона */
  @Input()
  public type: GasSectionType = 'base';

  @Input()
  public postfixIconName: string | GasIcon.Icons;

  /** цвет postfix иконки */
  @Input()
  public postfixIconColor: GasColor.Color | string;

  @Output()
  public activeNameChange: EventEmitter<string | string[]> = new EventEmitter();

  @Output()
  public postfixIconClicked = new EventEmitter<string>();

  @ContentChildren(GasAccordionSectionDirective)
  public sections: QueryList<GasAccordionSectionDirective>;

  public toggle(section: GasAccordionSectionDirective): void {
    const active = this.addOptionToSelection(section.name, this.activeName, this.multiple as boolean);
    this.activeNameChange.emit(active);
  }

  public isOpen(section: GasAccordionSectionDirective): boolean {
    return this.isActiveSection(section.name, this.activeName, this.multiple as boolean);
  }

  public onPostfixIconClick(sectionName: string): void {
    this.postfixIconClicked.emit(sectionName);
  }

  private addOptionToSelection(
    value: string,
    selection: string | string[],
    multiple: boolean,
    clearable: boolean = true,
  ): string | string[] {
    let next: string | string[];
    if (multiple) {
      if (!selection) {
        selection = [];
      }
      if (Array.isArray(selection)) {
        const index = selection.indexOf(value);
        next = this.activeName = index > -1 ?
          [...selection.slice(0, index), ...selection.slice(index + 1)] :
          [...selection, value];
      } else {
        next = Object.assign({}, selection, { [value]: !selection[value] });
      }
    } else {
      next = this.activeName = selection === value && clearable ? null : value;
    }

    return next;
  }

  private isActiveSection(value: string, selection: string | string[], multiple: boolean): boolean {
    if (multiple) {
      if (!selection) {
        return false;
      }

      return Array.isArray(selection) ?
        selection.indexOf(value) > -1 :
        value === selection;
    }

    return value === selection;
  }
}
